import { Link } from "react-router-dom";
import { css, cx } from "styled-system/css";
import { useEffect, useState } from "react";
import { Bell, Video, ArrowUp } from "lucide-react";
import { FadeIn, RevealOnScroll, Stagger, StaggerItem } from "@/components/ui/animated";
import { Skeleton } from "@/components/ui/Skeleton";
import { LandingParallaxSection } from "@/components/landing/landing-parallax-section";
import { IntegrationsMarquee } from "@/components/landing/integrations-marquee";
import { LandingNavbar } from "@/components/landing/landing-navbar";
import { InstallAppBanner } from "@/components/landing/install-app-banner";
import { LandingFaqItem } from "@/components/landing/landing-faq-item";
import { BillingCycleToggle } from "@/components/ui/billing-cycle-toggle";

type PublicLandingPlan = {
  id: string;
  code: string;
  name: string;
  priceLabel: string;
  periodLabel: string;
  description: string | null;
  highlights: unknown;
  isFeatured: boolean;
};

type PlanPricingItem = {
  plan: "FREE" | "PRO" | "ENTERPRISE";
  monthlyPrice: number;
  annualPrice: number;
  extraAgendaMonthly: number;
  extraAgendaAnnual: number;
};

type PublicPricingData = {
  pricing: PlanPricingItem[];
  annualBillingEnabled: boolean;
  savings: Record<string, number>;
};

function parseHighlights(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((x): x is string => typeof x === "string");
  return [];
}

// Spectral + IBM Plex Sans — scoped to the landing surface only.
// Mirrored from the design-kit ui_kits/landing/landing.css.
const LANDING_DISPLAY = '"Spectral", ui-serif, Georgia, serif';
const LANDING_SANS = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';

// ─── Rubros: bento mosaic ─────────────────────────────────────────────────────
type RubroItem = {
  title: string;
  image: string;
  imageAlt: string;
  colSpan?: number;
  rowSpan?: number;
};

const RUBROS: RubroItem[] = [
  { title: "Peluquerías",    image: "/landing/rubros/peluquerias.png",    imageAlt: "Salón de peluquería moderno con espejos iluminados y sillones profesionales.", colSpan: 2, rowSpan: 2 },
  { title: "Médicos",        image: "/landing/rubros/medicos.png",        imageAlt: "Equipo de médicos en guardapolvos blancos en entorno clínico." },
  { title: "Odontólogos",    image: "/landing/rubros/odontologos.png",    imageAlt: "Odontólogo realizando un procedimiento en consultorio." },
  { title: "Canchas",        image: "/landing/rubros/canchas.png",        imageAlt: "Complejo deportivo al aire libre con canchas de pádel.", colSpan: 2 },
  { title: "Psicólogos",     image: "/landing/rubros/psicologos.png",     imageAlt: "Sesión de consulta entre profesional y persona en un espacio cómodo.", rowSpan: 2 },
  { title: "Gimnasios",      image: "/landing/rubros/gimnasios.png",      imageAlt: "Gimnasio equipado con máquinas de entrenamiento y buena iluminación.", colSpan: 2, rowSpan: 2 },
  { title: "Masajistas",     image: "/landing/rubros/masajistas.png",     imageAlt: "Masaje terapéutico profesional en camilla." },
  { title: "Nutricionistas", image: "/landing/rubros/nutricionistas.png", imageAlt: "Profesional de la salud con alimentos frescos y consulta nutricional." },
];

function Landing() {
  const [publishedPlans, setPublishedPlans] = useState<PublicLandingPlan[] | null>(null);
  const [pricingData, setPricingData] = useState<PublicPricingData | null>(null);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [enterpriseAgendas, setEnterpriseAgendas] = useState(3);
  const [showTop, setShowTop] = useState(false);

  // Inject Google Fonts for the landing surface (Spectral + IBM Plex Sans).
  useEffect(() => {
    const id = "landing-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,500;0,600;0,700;0,800;1,500;1,600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  // Back-to-top visibility
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetch("/api/public/landing", { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error("landing");
        return r.json() as Promise<{ plans?: PublicLandingPlan[] }>;
      })
      .then((d) => {
        if (!cancelled) setPublishedPlans(Array.isArray(d.plans) ? d.plans : []);
      })
      .catch(() => {
        if (!cancelled) setPublishedPlans([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetch("/api/public/plan-pricing", { cache: "no-store" })
      .then(async (r) => { if (!r.ok) throw new Error("pricing"); return r.json() as Promise<PublicPricingData>; })
      .then((d) => { if (!cancelled) setPricingData(Array.isArray(d?.pricing) ? d : { ...d, pricing: [] }); })
      .catch(() => { if (!cancelled) setPricingData({ pricing: [], annualBillingEnabled: false } as unknown as PublicPricingData); });
    return () => { cancelled = true; };
  }, []);

  const container = css({ maxW: "1120px", mx: "auto", px: { base: "5", md: "8" } });
  const scrollAnchor = css({ scrollMarginTop: { base: "5.5rem", md: "6rem" } });

  const primaryDark = css({
    display: "inline-flex", alignItems: "center", gap: "2",
    px: "6", py: "3.5", rounded: "xl", fontSize: "md", fontWeight: "bold",
    color: "#0f172a", bg: "white", boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
    transition: "transform 0.16s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.16s ease",
    _hover: { transform: "translateY(-1px)", boxShadow: "0 8px 24px rgba(0,0,0,0.28)" },
    _active: { transform: "scale(0.97)" }, textDecoration: "none",
  });

  const ghostDark = css({
    display: "inline-flex", alignItems: "center", px: "6", py: "3.5",
    rounded: "xl", fontSize: "md", fontWeight: "semibold",
    borderWidth: "1px", borderColor: "rgba(255,255,255,0.35)",
    color: "rgba(255,255,255,0.88)", bg: "rgba(255,255,255,0.06)",
    transition: "background 0.18s ease, border-color 0.18s ease, transform 0.16s cubic-bezier(0.23, 1, 0.32, 1)",
    _hover: { bg: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.55)" },
    _active: { transform: "scale(0.97)" }, textDecoration: "none",
  });

  // Section-level heading (Spectral display) — fontSize applied inline via clamp()
  const sectionTitle = css({ fontWeight: "semibold", color: "#0F172A", letterSpacing: "-0.03em", lineHeight: "1.04" });
  const sectionSub = css({ fontSize: "17px", color: "#51607a", maxW: "58ch", lineHeight: "1.7", mt: "5" });
  const eyebrow = css({ fontSize: "12px", fontWeight: "600", letterSpacing: "0.16em", textTransform: "uppercase", color: "#4f46e5", m: 0 });

  return (
    <div className={css({ minH: "100dvh", bg: "white" })} style={{ fontFamily: LANDING_SANS }}>
      <LandingNavbar />

      {/* ── Hero ── */}
      <LandingParallaxSection
        variant="strong"
        className={css({ bg: "#0f172a", borderBottomWidth: "1px", borderColor: "rgba(255,255,255,0.06)" })}
      >
        <div className={cx(container, css({ pt: { base: "28", md: "36" }, pb: { base: "16", md: "24" } }))}>
          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", lg: "1.08fr 0.92fr" }, gap: { base: "12", lg: "14" }, alignItems: "center" })}>

            {/* Left: headline + CTAs */}
            <div>
              <FadeIn>
                <p className={css({ fontSize: "12px", fontWeight: "600", letterSpacing: "0.16em", textTransform: "uppercase", color: "#a5b4fc", m: 0 })}>
                  Agenda online · Argentina
                </p>
                <h1 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: "clamp(44px, 5.6vw, 72px)", lineHeight: 0.96, letterSpacing: "-0.035em", color: "#fff", margin: "22px 0 0" }}>
                  La forma más simple de gestionar tus turnos,{" "}
                  <em style={{ fontStyle: "italic", fontWeight: 500, color: "#a5b4fc" }}>sin complicaciones</em>.
                </h1>
              </FadeIn>

              <FadeIn delay={0.07}>
                <p className={css({ mt: "6", fontSize: "19px", color: "rgba(255,255,255,0.66)", lineHeight: "1.65", maxW: "50ch" })}>
                  Reservas online 24/7, recordatorios por WhatsApp y cobros con MercadoPago. Para cualquier profesional que quiera llenar su agenda y dejar de perder tiempo.
                </p>
              </FadeIn>

              <FadeIn delay={0.14}>
                <div className={css({ mt: "9", display: "flex", gap: "3", flexWrap: "wrap", alignItems: "center" })}>
                  <Link to="/auth/register" className={primaryDark}>
                    Crear mi cuenta gratis →
                  </Link>
                  <Link to="/demo-booking" className={ghostDark}>
                    Ver demo
                  </Link>
                </div>
                <p className={css({ mt: "5", fontSize: "xs", color: "rgba(255,255,255,0.38)", letterSpacing: "0.01em" })}>
                  Sin tarjeta de crédito · Gratis de por vida · Configuración en 5 minutos
                </p>
              </FadeIn>
            </div>

            {/* Right: mock app card */}
            <FadeIn delay={0.22}>
              <MockAppCard />
            </FadeIn>
          </div>
        </div>
      </LandingParallaxSection>

      {/* ── Instalar como app ── */}
      <InstallAppBanner />

      {/* ── Integrations marquee ── */}
      <LandingParallaxSection
        variant="strong"
        className={css({ borderTopWidth: "1px", borderBottomWidth: "1px", borderColor: "rgba(255,255,255,0.06)", py: { base: "8", md: "10" } })}
      >
        <div className={cx(container)}>
          <p className={css({ textAlign: "center", fontSize: "13px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.42)", m: "0 0 20px" })}>
            Se integra con las herramientas que ya usás
          </p>
          <IntegrationsMarquee />
        </div>
      </LandingParallaxSection>

      {/* ── Features bento ── */}
      <section
        id="soluciones"
        className={cx(css({ bg: "white", borderBottomWidth: "1px", borderColor: "#e7ebf0" }), scrollAnchor)}
      >
        <RevealOnScroll>
          <div className={cx(container, css({ py: { base: "20", md: "36" } }))}>
            <div className={css({ maxW: "60ch" })}>
              <p className={eyebrow}>Soluciones</p>
              <h2 style={{ fontFamily: LANDING_DISPLAY, marginTop: 14, fontSize: "clamp(30px, 4vw, 46px)" }} className={sectionTitle}>
                Diseñado para que la agenda se mueva sola
              </h2>
              <p className={sectionSub}>
                Cobrá señas, automatizá recordatorios y centralizá el calendario. Todo con una experiencia clara que tus clientes van a entender al instante.
              </p>
            </div>

            <Stagger>
              <div className={css({ mt: "13", display: "grid", gridTemplateColumns: { base: "1fr", lg: "2fr 1fr" }, gap: { base: "4", lg: "4.5" } })}>
                <StaggerItem>
                  <BentoStatCard
                    title="Menos inasistencias desde el primer mes."
                    kicker="Señas + MercadoPago"
                    bigNumber="82%"
                    body="Confirmá reservas con seña o pago parcial, sin comisiones sorpresa para tu cliente."
                  />
                </StaggerItem>
                <div className={css({ display: "grid", gap: { base: "4", lg: "4.5" } })}>
                  <StaggerItem>
                    <FeatureCard
                      icon={<Bell size={20} strokeWidth={1.75} />}
                      title="Recordatorios automáticos"
                      body="WhatsApp y email. Menos ausencias y menos llamadas para confirmar."
                    />
                  </StaggerItem>
                  <StaggerItem>
                    <FeatureCard
                      icon={<GoogleCalendarIcon />}
                      iconStyle="logo"
                      title="Google Calendar sincronizado"
                      body="Tus turnos en un solo lugar. Evitá dobles reservas sin esfuerzo."
                    />
                  </StaggerItem>
                </div>
              </div>
              <div className={css({ mt: { base: "4", lg: "4.5" } })}>
                <StaggerItem>
                  <MeetingModesCard title="Turnos presenciales, virtuales o mixtos." body="Ordená tu semana con vista día, semana y mes, y estados claros para cada reserva." />
                </StaggerItem>
              </div>
            </Stagger>
          </div>
        </RevealOnScroll>
      </section>

      {/* ── Rubros bento mosaic ── */}
      <section id="rubros" className={cx(css({ bg: "#f8fafc", borderTopWidth: "1px", borderColor: "rgba(226,232,240,0.8)" }), scrollAnchor)}>
        <RevealOnScroll>
          <div className={cx(container, css({ py: { base: "14", md: "24" } }))}>
            <p className={eyebrow}>Rubros</p>
            <h2 style={{ fontFamily: LANDING_DISPLAY, marginTop: 14, fontSize: "clamp(30px, 4vw, 46px)" }} className={sectionTitle}>
              Para cualquier profesional
            </h2>
            <p className={sectionSub}>
              Peluquerías · Odontólogos · Psicólogos · Canchas deportivas · Médicos · Gimnasios · Masajistas · Nutricionistas · y más.
            </p>

            {/* 4-col bento grid with dense auto-flow; collapses to 2-col on mobile */}
            <div
              className={css({
                mt: "11",
                display: "grid",
                gridTemplateColumns: { base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                gridAutoRows: { base: "144px", md: "150px" },
                gridAutoFlow: "dense",
                gap: "4",
              })}
            >
              {RUBROS.map((r) => (
                <RubroCard key={r.title} {...r} />
              ))}
            </div>
          </div>
        </RevealOnScroll>
      </section>

      {/* ── Testimonials ── */}
      <section className={css({ bg: "white", borderTopWidth: "1px", borderColor: "rgba(226,232,240,0.75)" })}>
        <RevealOnScroll>
          <div className={cx(container, css({ py: { base: "16", md: "28" } }))}>
            <p className={eyebrow}>Testimonios</p>
            <h2 style={{ fontFamily: LANDING_DISPLAY, marginTop: 14, fontSize: "clamp(30px, 4vw, 46px)" }} className={sectionTitle}>
              Lo que dicen nuestros usuarios
            </h2>

            {/* Featured pull quote */}
            <div className={css({ mt: "12", position: "relative", pl: { base: "0", md: "8" } })}>
              <span
                className={css({
                  display: "block", position: "absolute",
                  top: { base: "-16px", md: "-34px" }, left: { base: "-4px", md: "-10px" },
                  fontSize: { base: "7xl", md: "9xl" }, lineHeight: "1",
                  color: "#4338CA", opacity: 0.14, fontWeight: "bold",
                  pointerEvents: "none", userSelect: "none",
                })}
                style={{ fontFamily: LANDING_DISPLAY }}
                aria-hidden
              >
                "
              </span>
              <p
                style={{ fontFamily: LANDING_DISPLAY, fontWeight: 500 }}
                className={css({ position: "relative", zIndex: 1, fontSize: { base: "xl", md: "3xl" }, color: "#0f172a", lineHeight: "1.32", letterSpacing: "-0.01em", maxW: "22ch", m: 0 })}
              >
                Pasé de anotar en papel a llenar la agenda sola. Mis pacientes reservan solos.
              </p>
              <div className={css({ mt: "7", display: "flex", alignItems: "center", gap: "4" })}>
                <div className={css({ w: "10", h: "1px", bg: "#cbd5e1", flexShrink: 0 })} />
                <div>
                  <p className={css({ fontWeight: "bold", color: "#0f172a" })}>Dra. Martínez</p>
                  <p className={css({ fontSize: "sm", color: "#64748b" })}>Médica · @dramartinez</p>
                </div>
              </div>
            </div>

            {/* Mini testimonials */}
            <div className={css({ mt: "13", pt: "11", borderTopWidth: "1px", borderColor: "#e7ebf0", display: "grid", gridTemplateColumns: { base: "1fr", md: "1fr 1fr" }, gap: "9" })}>
              <MiniTestimonial name="Leo" role="Tatuajes" ig="@leo.ink" quote="Las señas online bajaron los ausentismos un montón. Se nota en la caja." />
              <MiniTestimonial name="Studio Belleza" role="Estética" ig="@studiobelleza" quote="Los recordatorios nos ahorran llamadas todo el día. Es un antes y un después." />
            </div>
          </div>
        </RevealOnScroll>
      </section>

      {/* ── Pricing ── */}
      <section
        id="planes"
        className={cx(css({ bg: "#f5f5ff", borderTopWidth: "1px", borderColor: "#e6e3ff" }), scrollAnchor)}
      >
        <RevealOnScroll>
          <div className={cx(container, css({ py: { base: "20", md: "40" } }))}>
            <p className={eyebrow}>Planes</p>
            <h2 style={{ fontFamily: LANDING_DISPLAY, marginTop: 14, fontSize: "clamp(30px, 4vw, 46px)" }} className={sectionTitle}>
              Elegí el plan que más te convenga
            </h2>
            <p className={sectionSub}>Sin permanencia. Podés cambiar de plan en cualquier momento.</p>

            {/* Billing toggle */}
            {pricingData?.annualBillingEnabled && (
              <div className={css({ mt: "10", display: "flex", justifyContent: "center" })}>
                <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />
              </div>
            )}

            <div className={css({ mt: "10", display: "grid", gap: "5", gridTemplateColumns: { base: "1fr", md: "repeat(3, minmax(0, 1fr))" }, alignItems: "start" })}>
              {pricingData === null ? (
                <><PriceCardSkeleton /><PriceCardSkeleton /><PriceCardSkeleton /></>
              ) : (
                <>
                  {/* Free */}
                  {(() => {
                    const highlights = publishedPlans?.find((p) => p.code === "FREE" || p.name.toLowerCase().includes("free"))?.highlights;
                    return (
                      <PriceCard
                        title="Free" price="Gratis" sub="para siempre" description="Ideal para empezar sin costo."
                        items={parseHighlights(highlights) ?? ["1 agenda", "Página de turnos online", "Recordatorios por email", "Hasta 100 turnos / mes"]}
                        cta="Empezar gratis" featured={false} registerTo="/auth/register?plan=FREE"
                      />
                    );
                  })()}

                  {/* Pro */}
                  {(() => {
                    const pro = pricingData.pricing.find((p) => p.plan === "PRO");
                    const price = pro ? (billingCycle === "annual" ? pro.annualPrice : pro.monthlyPrice) : 25000;
                    const proSavings = pro ? (pro.monthlyPrice - pro.annualPrice) * 12 : 72000;
                    const annualTotal = billingCycle === "annual" ? `$${(price * 12).toLocaleString("es-AR")}` : undefined;
                    const highlights = publishedPlans?.find((p) => p.code === "PRO" || p.name.toLowerCase().includes("pro"))?.highlights;
                    return (
                      <PriceCard
                        title="Pro" price={`$${price.toLocaleString("es-AR")}`} sub="/ mes"
                        description="Para profesionales que quieren crecer."
                        items={parseHighlights(highlights) ?? ["Hasta 3 agendas", "Turnos ilimitados", "Recordatorios por WhatsApp", "Integración MercadoPago", "Métricas y reportes", "Soporte prioritario"]}
                        cta="Elegir Pro" featured badge={billingCycle === "annual" ? `Ahorrás $${proSavings.toLocaleString("es-AR")} al año` : "Más popular"}
                        registerTo="/auth/register?plan=PRO" annualTotal={annualTotal}
                      />
                    );
                  })()}

                  {/* Enterprise */}
                  {(() => {
                    const ent = pricingData.pricing.find((p) => p.plan === "ENTERPRISE");
                    const basePrice = ent ? (billingCycle === "annual" ? ent.annualPrice : ent.monthlyPrice) : 25000;
                    const extraPrice = ent ? (billingCycle === "annual" ? ent.extraAgendaAnnual : ent.extraAgendaMonthly) : 30000;
                    const extraCount = Math.max(0, enterpriseAgendas - 3);
                    const total = basePrice + extraCount * extraPrice;
                    const annualTotal = billingCycle === "annual" ? total * 12 : null;
                    const highlights = publishedPlans?.find((p) => p.code === "ENTERPRISE" || p.name.toLowerCase().includes("enterprise"))?.highlights;
                    return (
                      <div className={css({ display: "flex", flexDir: "column", bg: "white", borderWidth: "1px", borderColor: "#e2e8f0", p: "9", transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease", _hover: { transform: "translateY(-4px)", boxShadow: "0 22px 50px rgba(15,23,42,0.10)" } })} style={{ borderRadius: 22 }}>
                        <p style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 22, margin: 0, color: "#111827" }}>Enterprise</p>
                        <p className={css({ mt: "1.5", fontSize: "sm", color: "#64748b", lineHeight: "1.5", minH: "10" })}>Para clínicas, estudios y negocios con muchos profesionales.</p>
                        <div style={{ marginTop: 14 }}>
                          <span style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 46, color: "#111827", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
                            ${total.toLocaleString("es-AR")}
                          </span>
                          {" "}<span className={css({ fontSize: "sm", fontWeight: "500", color: "#94a3b8" })}>/mes</span>
                          {annualTotal && <p className={css({ mt: "1", fontSize: "xs", color: "#94a3b8" })}>${annualTotal.toLocaleString("es-AR")} facturado anualmente</p>}
                        </div>

                        {/* Agenda stepper */}
                        <div className={css({ bg: "#f8fafc", rounded: "xl", p: "4", mb: "6", borderWidth: "1px", borderColor: "#e2e8f0" })}>
                          <p className={css({ fontSize: "xs", color: "#64748b", mb: "3", fontWeight: "semibold" })}>Agendas incluidas</p>
                          <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
                            <button
                              type="button"
                              onClick={() => setEnterpriseAgendas((n) => Math.max(3, n - 1))}
                              className={css({ w: "8", h: "8", rounded: "full", bg: enterpriseAgendas <= 3 ? "#e2e8f0" : "#f1f5f9", color: enterpriseAgendas <= 3 ? "#94a3b8" : "#0f172a", fontSize: "lg", fontWeight: "bold", cursor: enterpriseAgendas <= 3 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s ease" })}
                            >-</button>
                            <span className={css({ fontSize: "lg", fontWeight: "bold", color: "#0f172a", minW: "4", textAlign: "center" })}>{enterpriseAgendas}</span>
                            <button
                              type="button"
                              onClick={() => setEnterpriseAgendas((n) => n + 1)}
                              className={css({ w: "8", h: "8", rounded: "full", bg: "#f1f5f9", color: "#0f172a", fontSize: "lg", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s ease", _hover: { bg: "#e2e8f0" } })}
                            >+</button>
                            <span className={css({ fontSize: "xs", color: "#94a3b8" })}>
                              {enterpriseAgendas === 3 ? "incluidas en el plan base" : `3 base + ${extraCount} extra${extraCount > 1 ? "s" : ""} × $${extraPrice.toLocaleString("es-AR")}`}
                            </span>
                          </div>
                        </div>

                        <ul className={css({ display: "flex", flexDir: "column", gap: "2.5", mb: "8", flex: "1" })}>
                          {(parseHighlights(highlights).length > 0
                            ? parseHighlights(highlights)
                            : ["Agendas ilimitadas", "Turnos ilimitados", "WhatsApp + email", "MercadoPago", "Métricas avanzadas", "Soporte prioritario", "Factura A disponible"]
                          ).map((item) => (
                            <li key={item} className={css({ display: "flex", alignItems: "flex-start", gap: "2.5", fontSize: "sm", color: "#334155" })}>
                              <span className={css({ color: "#4338CA", mt: "0.5", flexShrink: 0 })}>✓</span>
                              {item}
                            </li>
                          ))}
                        </ul>

                        <a
                          href="/auth/register?plan=ENTERPRISE"
                          className={css({ display: "flex", alignItems: "center", justifyContent: "center", px: "5", py: "3.5", fontSize: "15px", fontWeight: "bold", color: "#44506b", textDecoration: "none", transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1), color 0.25s ease", _hover: { transform: "translateY(-2px)", color: "#111827" }, _active: { transform: "scale(0.97)" } })}
                          style={{ borderRadius: 11, border: "1px solid #e7ebf0" }}
                        >
                          Contactar ventas
                        </a>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </RevealOnScroll>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className={cx(scrollAnchor)}>
        <RevealOnScroll>
          <div className={cx(container, css({ py: { base: "14", md: "24" } }))}>
            <p className={eyebrow}>Preguntas frecuentes</p>
            <h2 style={{ fontFamily: LANDING_DISPLAY, marginTop: 14, fontSize: "clamp(30px, 4vw, 46px)" }} className={sectionTitle}>
              Todo lo que necesitás saber
            </h2>
            <div className={css({ mt: "10", display: "grid", gridTemplateColumns: { base: "1fr", md: "1fr 1fr" }, columnGap: "8", rowGap: "3" })}>
              <LandingFaqItem q="¿Necesito tarjeta para empezar?" a="No. Podés crear tu cuenta y configurarlo en minutos, sin ingresar ningún dato de pago." />
              <LandingFaqItem q="¿Puedo cobrar seña?" a="Sí. Configurás el monto o porcentaje por servicio y tus clientes pagan al reservar." />
              <LandingFaqItem q="¿Funciona para cualquier rubro?" a="Sí: salud, estética, deportes, bienestar y más. Se adapta a tu forma de trabajar." />
              <LandingFaqItem q="¿Se integra con Google Calendar?" a="Sí. Podés sincronizar tus turnos y bloquear ocupaciones externas automáticamente." />
            </div>
          </div>
        </RevealOnScroll>
      </section>

      {/* ── CTA ── */}
      <LandingParallaxSection
        variant="strong"
        className={css({ borderTopWidth: "1px", borderColor: "rgba(255,255,255,0.06)" })}
      >
        <RevealOnScroll y={20}>
          <div className={cx(container, css({ py: { base: "20", md: "32" }, textAlign: "center" }))}>
            <h2 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600 }} className={css({ fontSize: { base: "3xl", md: "5xl" }, fontWeight: "semibold", color: "white", letterSpacing: "-0.015em" })}>
              Empezá gratis hoy. Sin tarjeta.
            </h2>
            <p className={css({ mt: "5", color: "rgba(255,255,255,0.65)", fontSize: { base: "sm", md: "md" } })}>
              Listo en 5 minutos.
            </p>
            <div className={css({ mt: "10" })}>
              <Link
                to="/auth/register"
                className={css({
                  display: "inline-flex", alignItems: "center", gap: "2", px: "8", py: "4", rounded: "2xl",
                  fontWeight: "bold", fontSize: "md", bg: "white", color: "#4338CA",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
                  transition: "transform 0.16s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.16s ease",
                  _hover: { transform: "translateY(-2px)", boxShadow: "0 12px 32px rgba(0,0,0,0.3)" },
                  _active: { transform: "scale(0.97)" }, textDecoration: "none",
                })}
              >
                Crear mi cuenta →
              </Link>
            </div>
          </div>
        </RevealOnScroll>
      </LandingParallaxSection>

      {/* ── Footer ── */}
      <footer className={css({ bg: "#080d18", color: "rgba(255,255,255,0.7)" })}>
        <div className={cx(container, css({ py: { base: "14", md: "16" }, display: "grid", gridTemplateColumns: { base: "1fr", md: "1.4fr 1fr 1fr" }, gap: "10" }))}>
          <div>
            <div className={css({ display: "flex", alignItems: "center", gap: "2.5" })}>
            <img src="/logo-mark.png" alt="" width={26} height={26} style={{ display: "block" }} />
            <p className={css({ fontWeight: "bold", fontSize: "lg", color: "white", m: 0 })}>SFTURNOS</p>
          </div>
            <p className={css({ mt: "2", fontSize: "sm", color: "rgba(255,255,255,0.5)", lineHeight: "1.6", maxW: "34ch" })}>
              Gestión de turnos simple, para profesionales argentinos.
            </p>
          </div>
          <div>
            <p className={css({ fontSize: "xs", fontWeight: "bold", letterSpacing: "0.14em", color: "rgba(255,255,255,0.42)" })}>SOLUCIONES</p>
            <div className={css({ mt: "3", display: "grid", gap: "1.5", fontSize: "sm" })}>
              <a href="#soluciones" className={css({ color: "rgba(255,255,255,0.6)", _hover: { color: "white" } })}>Señas + pagos</a>
              <a href="#soluciones" className={css({ color: "rgba(255,255,255,0.6)", _hover: { color: "white" } })}>Recordatorios</a>
              <a href="#soluciones" className={css({ color: "rgba(255,255,255,0.6)", _hover: { color: "white" } })}>Calendario</a>
            </div>
          </div>
          <div>
            <p className={css({ fontSize: "xs", fontWeight: "bold", letterSpacing: "0.14em", color: "rgba(255,255,255,0.42)" })}>LEGAL</p>
            <div className={css({ mt: "3", display: "grid", gap: "1.5", fontSize: "sm" })}>
              <span className={css({ color: "rgba(255,255,255,0.6)" })}>Términos</span>
              <Link to="/privacidad" className={css({ color: "rgba(255,255,255,0.6)", textDecoration: "none", _hover: { color: "white" } })}>Privacidad</Link>
            </div>
          </div>
        </div>
        <div className={css({ borderTopWidth: "1px", borderColor: "rgba(255,255,255,0.07)" })}>
          <div className={cx(container, css({ py: "5", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "3" }))}>
            <p className={css({ fontSize: "sm", color: "rgba(255,255,255,0.4)", m: 0 })}>© {new Date().getFullYear()} SFTURNOS</p>
            <a href="https://sftdevelopment.com/" target="_blank" rel="noopener noreferrer" className={css({ fontSize: "xs", color: "rgba(255,255,255,0.35)", textDecoration: "none", letterSpacing: "0.04em", transition: "color 0.18s ease", _hover: { color: "rgba(255,255,255,0.65)" } })}>
              Powered by SFT Development
            </a>
          </div>
        </div>
      </footer>

      {/* ── Back-to-top ── */}
      <button
        type="button"
        aria-label="Volver arriba"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={css({
          position: "fixed", right: "6", bottom: "6", zIndex: 60,
          w: "12", h: "12", rounded: "full",
          bg: "#0f172a", color: "white",
          borderWidth: "1px", borderColor: "rgba(255,255,255,0.14)",
          display: "grid", placeItems: "center", cursor: "pointer",
          boxShadow: "0 8px 24px rgba(15,23,42,0.24)",
          opacity: showTop ? 1 : 0,
          transform: showTop ? "translateY(0) scale(1)" : "translateY(12px) scale(0.9)",
          pointerEvents: showTop ? "auto" : "none",
          transition: "opacity 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), background 0.25s ease",
          _hover: { bg: "#000", transform: showTop ? "translateY(-2px)" : "translateY(12px) scale(0.9)" },
          _active: { transform: "scale(0.94)" },
        })}
      >
        <ArrowUp size={20} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function MockRow({ tone, title, subtitle, right }: { tone: "confirmed" | "pending" | "completed"; title: string; subtitle: string; right: string }) {
  const map = {
    confirmed: { bar: "#818cf8", bg: "rgba(129,140,248,0.12)" },
    pending:   { bar: "#fbbf24", bg: "rgba(251,191,36,0.1)" },
    completed: { bar: "#34d399", bg: "rgba(52,211,153,0.1)" },
  }[tone];
  return (
    <div className={css({ display: "grid", gridTemplateColumns: "3px 1fr auto", gap: "10px", alignItems: "center", rounded: "xl", p: "3" })} style={{ background: map.bg }}>
      <div className={css({ w: "3px", h: "full", rounded: "full" })} style={{ background: map.bar }} />
      <div className={css({ minW: 0 })}>
        <p className={css({ fontWeight: "bold", color: "white", fontSize: "sm", truncate: true })}>{title}</p>
        <p className={css({ fontSize: "xs", color: "rgba(255,255,255,0.55)", truncate: true })}>{subtitle}</p>
      </div>
      <span className={css({ fontSize: "xs", fontWeight: "bold", color: "rgba(255,255,255,0.8)", minW: 0, textAlign: "right" })}>{right}</span>
    </div>
  );
}

function MockAppCard() {
  return (
    <div className={css({ rounded: "2xl", bg: "rgba(15, 20, 40, 0.7)", borderWidth: "1px", borderColor: "rgba(129, 140, 248, 0.2)", backdropFilter: "blur(16px)", overflow: "hidden", boxShadow: "0 40px 100px rgba(0,0,0,0.5), 0 0 0 1px rgba(129,140,248,0.1) inset" })}>
      <div
        className={css({ px: "6", pt: "5", pb: "6", position: "relative", overflow: "hidden" })}
        style={{ background: "linear-gradient(135deg, #4338ca, #4f46e5)" }}
      >
        <div className={css({ position: "absolute", top: "-40px", right: "-40px", w: "160px", h: "160px", pointerEvents: "none" })} style={{ background: "radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)" }} aria-hidden />
        <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4", position: "relative" })}>
          <p className={css({ fontSize: "12px", color: "rgba(255,255,255,0.7)", fontWeight: "semibold" })}>Agenda del día</p>
          <span className={css({ fontSize: "12px", color: "rgba(255,255,255,0.45)" })}>Hoy</span>
        </div>
        <div className={css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4", position: "relative" })}>
          <div>
            <p style={{ fontFamily: LANDING_DISPLAY, fontWeight: 700, fontSize: 34, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>5</p>
            <p className={css({ fontSize: "12px", color: "rgba(255,255,255,0.65)", mt: "1.5" })}>turnos hoy</p>
          </div>
          <div className={css({ textAlign: "right" })}>
            <p style={{ fontFamily: LANDING_DISPLAY, fontWeight: 700, fontSize: 34, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>$75k</p>
            <p className={css({ fontSize: "12px", color: "rgba(255,255,255,0.65)", mt: "1.5" })}>facturado hoy</p>
          </div>
        </div>
      </div>
      <div className={css({ p: "5", display: "grid", gap: "2" })}>
        <MockRow tone="confirmed" title="Camila R." subtitle="Corte + barba" right="$12.000 ✓" />
        <MockRow tone="pending"   title="Lucas M."  subtitle="Color + brushing" right="Seña pendiente" />
        <MockRow tone="completed" title="Dra. Pérez" subtitle="Consulta" right="$20.000 ✓" />
      </div>
    </div>
  );
}

function GoogleCalendarIcon() {
  return (
    <svg viewBox="0 0 48 48" width="30" height="30" aria-hidden="true">
      <path fill="#4285f4" d="M37 1H4.5C2.01 1 0 3.01 0 5.5V37h11V12h26z"/>
      <path fill="#1967d2" d="M48 12V5.5C48 3.01 45.99 1 43.5 1H37v11z"/>
      <path fill="#fbbc04" d="M48 12H37v26h11z"/>
      <path fill="#ea4335" d="M37 48l11-11H37z"/>
      <path fill="#34a853" d="M37 37H11v11h26z"/>
      <path fill="#188038" d="M0 37v6.5C0 45.99 2.01 48 4.5 48H11V37z"/>
      <path fill="#fff" d="M11 12h26v25H11z"/>
      <text x="24" y="31" textAnchor="middle" fontFamily="'IBM Plex Sans',Arial,sans-serif" fontSize="13" fontWeight="700" fill="#4285f4">31</text>
    </svg>
  );
}

function BentoStatCard({ kicker, title, body, bigNumber }: { kicker: string; title: string; body: string; bigNumber: string }) {
  return (
    <div className={css({ rounded: "2xl", bg: "#f4f3ff", p: "11", position: "relative", overflow: "hidden", height: "100%", borderWidth: "1px", borderColor: "#e6e3ff" })}>
      <div className={css({ position: "absolute", bottom: "-70px", right: "-70px", w: "260px", h: "260px", pointerEvents: "none" })} style={{ background: "radial-gradient(circle, rgba(99,102,241,0.14), transparent 65%)" }} aria-hidden />
      <span className={css({ display: "inline-block", fontSize: "12px", fontWeight: "600", color: "#4338CA", bg: "rgba(67,56,202,0.10)", px: "3", py: "1.5", rounded: "full" })}>{kicker}</span>
      <div style={{ fontFamily: LANDING_DISPLAY, fontSize: "clamp(72px, 10vw, 112px)", fontWeight: 600, color: "#4338ca", letterSpacing: "-0.03em", lineHeight: 0.9, marginTop: 22 }}>
        {bigNumber}
      </div>
      <h3 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 26, color: "#1e1b4b", letterSpacing: "-0.01em", margin: "18px 0 0" }}>{title}</h3>
      <p className={css({ mt: "3", color: "#51607a", lineHeight: "1.65", maxW: "40ch" })}>{body}</p>
    </div>
  );
}

function FeatureCard({ icon, iconStyle = "default", title, body }: { icon: React.ReactNode; iconStyle?: "default" | "logo"; title: string; body: string }) {
  const icoClass = iconStyle === "logo"
    ? css({ w: "46px", h: "46px", rounded: "xl", bg: "white", borderWidth: "1px", borderColor: "#e7ebf0", display: "grid", placeItems: "center", mb: "4" })
    : css({ w: "46px", h: "46px", rounded: "xl", bg: "#eef2ff", display: "grid", placeItems: "center", mb: "4", color: "#4338CA" });
  return (
    <div className={css({ rounded: "2xl", bg: "white", borderWidth: "1px", borderColor: "#e2e8f0", p: "7", height: "100%", transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease, border-color 0.4s ease", _hover: { transform: "translateY(-4px)", boxShadow: "0 22px 50px rgba(15,23,42,0.10)", borderColor: "#dde3ec" } })}>
      <div className={icoClass}>{icon}</div>
      <h3 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 21, letterSpacing: "-0.01em", margin: 0 }}>{title}</h3>
      <p className={css({ mt: "2", color: "#51607a", lineHeight: "1.6", fontSize: "15px" })}>{body}</p>
    </div>
  );
}

function GoogleMeetIcon() {
  return (
    <svg viewBox="0 0 48 36" width="18" height="14" aria-hidden="true">
      <path fill="#00ac47" d="M34 12.5l9.8-5.5A1.6 1.6 0 0 1 46.2 8.4v19.2a1.6 1.6 0 0 1-2.4 1.4L34 23.5z"/>
      <path fill="#00832d" d="M7 4h22a4 4 0 0 1 4 4v20a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z"/>
      <path fill="#4285f4" d="M7 4h8v11H3V8a4 4 0 0 1 4-4z"/>
      <path fill="#ea4335" d="M3 15h12v6H3z"/>
      <path fill="#ffba00" d="M3 21h12v11H7a4 4 0 0 1-4-4z"/>
    </svg>
  );
}

function MeetingModesCard({ title, body }: { title: string; body: string }) {
  return (
    <div className={css({ rounded: "2xl", bg: "white", borderWidth: "1px", borderColor: "#e2e8f0", p: "7", display: "grid", gridTemplateColumns: { base: "1fr", md: "auto 1fr" }, gap: "6", alignItems: "center", transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease, border-color 0.4s ease", _hover: { transform: "translateY(-4px)", boxShadow: "0 22px 50px rgba(15,23,42,0.10)", borderColor: "#dde3ec" } })}>
      <div className={css({ w: { base: "full", md: "72px" }, h: { base: "56px", md: "72px" }, rounded: "2xl", bg: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#4338CA" })}>
        <Video size={30} strokeWidth={1.5} />
      </div>
      <div>
        <h3 style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 22, letterSpacing: "-0.01em", margin: 0 }}>{title}</h3>
        <p className={css({ mt: "2", color: "#51607a", lineHeight: "1.6", fontSize: "15px" })}>{body}</p>
        <div className={css({ mt: "4", display: "flex", gap: "2", flexWrap: "wrap" })}>
          <span className={css({ display: "inline-flex", alignItems: "center", px: "3.5", py: "1.5", rounded: "full", bg: "#f3f5f9", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "13px", fontWeight: "semibold", color: "#51607a" })}>
            Presencial
          </span>
          <span className={css({ display: "inline-flex", alignItems: "center", gap: "1.5", px: "3.5", py: "1.5", rounded: "full", bg: "#f3f5f9", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "13px", fontWeight: "semibold", color: "#51607a" })}>
            <GoogleMeetIcon />
            Google Meet
          </span>
          <span className={css({ display: "inline-flex", alignItems: "center", px: "3.5", py: "1.5", rounded: "full", bg: "#f3f5f9", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "13px", fontWeight: "semibold", color: "#51607a" })}>
            Por link
          </span>
        </div>
      </div>
    </div>
  );
}

// Pre-defined span classes — PandaCSS generates responsive CSS at build time.
const rubroCol2 = css({ gridColumn: { base: "auto", md: "span 2" } });
const rubroRow2 = css({ gridRow: { base: "auto", md: "span 2" } });

function RubroCard({ title, image, imageAlt, colSpan, rowSpan }: RubroItem) {
  return (
    <div
      className={cx(
        colSpan === 2 ? rubroCol2 : "",
        rowSpan === 2 ? rubroRow2 : "",
        css({ position: "relative", rounded: "2xl", overflow: "hidden", borderWidth: "1px", borderColor: "#e2e8f0", bg: "white", _hover: { "& img": { transform: "scale(1.05)" } } }),
      )}
    >
      <img
        src={image}
        alt={imageAlt}
        loading="lazy"
        decoding="async"
        className={css({ w: "full", h: "full", objectFit: "cover", objectPosition: "center", transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)" })}
      />
      {/* Gradient overlay */}
      <div className={css({ position: "absolute", inset: 0, bg: "linear-gradient(to top, rgba(11,18,32,0.72) 0%, transparent 52%)" })} />
      <span
        className={css({ position: "absolute", left: "4", bottom: "3.5", zIndex: 1, color: "white", fontWeight: "semibold", fontSize: "md" })}
        style={{ fontFamily: LANDING_DISPLAY, letterSpacing: "0.005em" }}
      >
        {title}
      </span>
    </div>
  );
}

function MiniTestimonial({ name, role, ig, quote }: { name: string; role: string; ig: string; quote: string }) {
  return (
    <div>
      <p className={css({ color: "#334155", lineHeight: "1.6", fontSize: "18px", m: 0 })}>"{quote}"</p>
      <p className={css({ mt: "4", mb: 0, fontSize: "sm", color: "#64748b" })}>
        <b style={{ color: "#111827", fontWeight: 600 }}>{name}</b>
        {" · "}{role}{" · "}{ig}
      </p>
    </div>
  );
}

function PriceCardSkeleton() {
  return (
    <div className={css({ rounded: "2xl", bg: "white", borderWidth: "1px", borderColor: "#e2e8f0", p: "7", minH: "280px" })}>
      <Skeleton className={css({ h: "5", w: "32", mb: "4" })} />
      <Skeleton className={css({ h: "10", w: "48", mb: "6" })} />
      <div className={css({ display: "grid", gap: "2" })}>
        <Skeleton /><Skeleton /><Skeleton className={css({ w: "80%" })} />
      </div>
    </div>
  );
}

function PriceCard({
  title, price, sub, items, cta, featured, badge, description, registerTo, annualTotal,
}: {
  title: string; price: string; sub: string; items: string[]; cta: string; featured: boolean;
  badge?: string; description?: string | null; registerTo?: string; annualTotal?: string;
}) {
  const href = registerTo ?? "/auth/register";

  // ── Shared base card ──────────────────────────────────────────────────────
  const cardWrap = css({
    display: "flex", flexDir: "column",
    bg: "white",
    borderWidth: featured ? "1.5px" : "1px",
    borderColor: featured ? "#4f46e5" : "#e2e8f0",
    p: "9",
    position: "relative",
    transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease",
    _hover: { transform: "translateY(-4px)", boxShadow: "0 22px 50px rgba(15,23,42,0.10)" },
  });

  return (
    <div
      className={cardWrap}
      style={{
        borderRadius: 22,
        boxShadow: featured ? "0 24px 60px rgba(67,56,202,0.16)" : undefined,
      }}
    >
      {/* Popular badge — absolute top-right */}
      {featured && badge && (
        <span
          className={css({
            position: "absolute", top: "4", right: "5",
            fontSize: "12px", fontWeight: "600", color: "white",
            bg: "#4f46e5", px: "3", py: "1.5", rounded: "full",
          })}
        >
          {badge}
        </span>
      )}

      {/* Tier name */}
      <p style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 22, margin: 0, color: "#111827" }}>{title}</p>
      {description && <p className={css({ mt: "1.5", fontSize: "sm", color: "#64748b", lineHeight: "1.5", minH: "10" })}>{description}</p>}

      {/* Price */}
      <div style={{ marginTop: 14 }}>
        <span style={{ fontFamily: LANDING_DISPLAY, fontWeight: 600, fontSize: 46, color: "#111827", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
          {price}
        </span>
        {" "}
        <span className={css({ fontSize: "sm", fontWeight: "500", color: "#94a3b8" })}>{sub}</span>
      </div>
      {annualTotal && <p className={css({ mt: "1", fontSize: "xs", color: "#94a3b8" })}>{annualTotal} facturado anualmente</p>}

      {/* Feature list */}
      <ul className={css({ display: "grid", gap: "2.5", pl: 0, listStyle: "none", flex: "1", mt: "5", mb: "7" })}>
        {items.map((i) => (
          <li key={i} className={css({ display: "flex", gap: "2.5", alignItems: "flex-start", fontSize: "15px", color: "#334155", lineHeight: "1.4" })}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M3.5 9L7.5 13L14.5 5.5" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {i}
          </li>
        ))}
      </ul>

      {/* CTA */}
      <Link
        to={href}
        className={css({
          display: "inline-flex", width: "100%", justifyContent: "center",
          px: "5", py: "3.5", fontWeight: "bold", fontSize: "15px",
          textDecoration: "none",
          transition: "transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease, background 0.3s ease",
          _hover: { transform: "translateY(-2px)" },
          _active: { transform: "scale(0.97)" },
        })}
        style={{
          borderRadius: 11,
          background: featured ? "#111827" : "transparent",
          color: featured ? "#fff" : "#44506b",
          border: featured ? "none" : "1px solid #e7ebf0",
          boxShadow: featured ? "0 1px 2px rgba(0,0,0,.16)" : "none",
        }}
      >
        {cta}
      </Link>
      {featured && <p className={css({ mt: "3", textAlign: "center", fontSize: "xs", color: "#94a3b8" })}>Sin tarjeta de crédito</p>}
    </div>
  );
}

export function LandingPage() {
  return <Landing />;
}
